import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Heart, MessageCircle, Send, MoreHorizontal, Smile, Film, Image, X, Loader2, Languages, CornerDownRight, AlertCircle, Repeat2 } from 'lucide-react';
import { useNostrEvent, useNostrQuery, useNostrProfile } from '../hooks/useNostrQuery';
import { useDisplayName } from '../hooks/useNip05Verified';
import { useNostrPublish } from '../hooks/useNostrPublish';
import { useAppContext } from '../hooks/useAppContext';
import PostCard, { useCommunityGate, TipSocialProof, computeTipSummary } from '../components/PostCard';
import { resolveTipTokens } from '../lib/tipTokens';
import NoteContent from '../components/NoteContent';
import BCHTipButton from '../components/BCHTipButton';
import UserBadges from '../components/UserBadges';
import EmojiPicker from '../components/EmojiPicker';
import GifPicker from '../components/GifPicker';
import MentionTextarea, { type MentionTextareaRef } from '../components/MentionTextarea';
import { uploadMedia, type CompressionProgress } from '../lib/imageUpload';
import { mightNeedTranslation, translateText, getLanguageName, type TranslationResult } from '../lib/translate';
import { timeAgo } from '../lib/utils';
import { nip19 } from 'nostr-tools';
import { COMMUNITY_POST_KIND, COMMUNITY_DEFINITION_KIND, communityATag } from '../lib/communities';

// ── Helper: extract the direct parent event ID from an event's tags ───────────

function getParentEventId(event: { tags: string[][] } | null): string | null {
  if (!event) return null;
  const quotedIds = new Set(event.tags.filter(t => t[0] === 'q').map(t => t[1]));
  const eTags = event.tags.filter(t => t[0] === 'e' && !quotedIds.has(t[1]) && t[3] !== 'edit');
  if (eTags.length === 0) return null;
  // Prefer explicitly-marked 'reply' tag
  const replyTag = eTags.find(t => t[3] === 'reply');
  if (replyTag) return replyTag[1];
  // Then 'root' only if it's the sole e-tag (shallow reply)
  const rootTag = eTags.find(t => t[3] === 'root');
  if (rootTag && eTags.length === 1) return rootTag[1];
  // No markers: last e-tag is the reply target (NIP-10)
  if (!eTags.some(t => t[3] === 'mention')) return eTags[eTags.length - 1][1];
  return null;
}

// ── Ancestor chain: recursively fetch parents up to root ─────────────────────
// Renders the full thread context above the current post.

function AncestorChain({ eventId, maxDepth = 20 }: { eventId: string; maxDepth?: number }) {
  // allRelays: parent posts may live outside the default relay subset.
  const { event, loading } = useNostrEvent(eventId, true);
  const parentId = getParentEventId(event);

  // Recursively render the parent's parent first (chronological order: root at top)
  return (
    <>
      {parentId && maxDepth > 0 && (
        <AncestorChain eventId={parentId} maxDepth={maxDepth - 1} />
      )}
      {loading ? (
        <div className="border-b border-border px-4 py-3 flex items-center gap-2 text-xs text-muted-foreground">
          <div className="w-4 h-4 border-2 border-muted-foreground/20 border-t-muted-foreground rounded-full animate-spin" />
          Loading parent post...
        </div>
      ) : event ? (
        <div className="relative">
          {/* Thread connector line */}
          <div className="absolute left-[2.05rem] top-[3.25rem] bottom-0 w-0.5 bg-border" />
          <PostCard event={event} showReplyTo={false} />
        </div>
      ) : (
        <div className="border-b border-border px-4 py-3">
          <div className="flex items-center gap-2 text-xs text-muted-foreground bg-muted/50 rounded-lg px-3 py-2">
            <AlertCircle className="w-3.5 h-3.5 shrink-0" />
            <span>Original post unavailable</span>
          </div>
        </div>
      )}
    </>
  );
}

// ── AncestorSection: conditionally renders the ancestor chain ────────────────

function AncestorSection({ event }: { event: { tags: string[][] } }) {
  const parentId = getParentEventId(event);
  if (!parentId) return null;
  return (
    <div className="bg-muted/10">
      <AncestorChain eventId={parentId} />
    </div>
  );
}

// ── NotePage ──────────────────────────────────────────────────────────────────

export default function NotePage() {
  const { noteId: rawNoteId } = useParams<{ noteId: string }>();
  const { user, setShowLoginDialog } = useAppContext();
  const navigate = useNavigate();

  // The route param may be a raw hex id OR a nip19 note1/nevent1 string.
  // Relay `ids` filters only match hex, so decode nip19 forms up front —
  // otherwise the query never matches and the page shows "Post not found".
  const noteId = useMemo(() => {
    if (!rawNoteId) return rawNoteId;
    if (/^[0-9a-f]{64}$/i.test(rawNoteId)) return rawNoteId.toLowerCase();
    try {
      const decoded = nip19.decode(rawNoteId);
      if (decoded.type === 'note') return decoded.data as string;
      if (decoded.type === 'nevent') return (decoded.data as { id: string }).id;
    } catch {
      // fall through — use as-is
    }
    return rawNoteId;
  }, [rawNoteId]);

  // Query across all read relays — the note may live on relays outside the
  // default 3-relay subset (this is why some linked notes showed
  // "can't find post" when the note actually exists).
  const { event, loading } = useNostrEvent(noteId, true);

  // Community gate: if this post belongs to a community, only members can reply
  const { isCommunityPost, isMember: isCommunityMember, communityId } = useCommunityGate(event, user?.pubkey);
  const communityReplyBlocked = isCommunityPost && !isCommunityMember;

  // Determine the thread root ID so we can fetch the ENTIRE thread tree,
  // not just direct children. NIP-10: look for a 'root' e-tag on the current event.
  const threadRootId = useMemo(() => {
    if (!event) return noteId;
    const rootTag = event.tags.find((t: string[]) => t[0] === 'e' && t[3] === 'root');
    return rootTag ? rootTag[1] : noteId;
  }, [event, noteId]);

  // Fetch ALL events referencing the root event ID — this captures the full thread
  // tree regardless of depth. Also fetch events referencing the current noteId
  // in case the root detection fails or the current post IS the root.
  //
  // We fetch BOTH kind 1 (plain replies) and kind 1111 (NIP-72/NIP-22
  // community comments). Comments on a community post are published as
  // kind 1111 so they carry the community tag — without including that kind
  // here they would be invisible in the thread ("disappearing comments").
  // Use allRelays so comments posted from other clients/relays are always found.
  const { events: allRelated, loading: repliesLoading, refetch } = useNostrQuery({
    filters: noteId ? [
      { kinds: [1, COMMUNITY_POST_KIND], '#e': [noteId], limit: 500 },
      ...(threadRootId && threadRootId !== noteId ? [{ kinds: [1, COMMUNITY_POST_KIND], '#e': [threadRootId], limit: 500 }] : []),
    ] : [],
    enabled: !!noteId,
    allRelays: true,
  });

  // Build parent→children map using NIP-10 marker logic
  const { rootReplies, childrenOf } = useMemo(() => {
    if (!noteId || allRelated.length === 0) {
      return { rootReplies: [], childrenOf: new Map<string, any[]>() };
    }
    const seen = new Set<string>();
    const events = allRelated.filter(e => {
      if (seen.has(e.id)) return false;
      seen.add(e.id);
      // Filter out edited versions of TOP-LEVEL posts that ended up here
      // because their edit-reference e-tag matched the '#e' relay query.
      // An edited post that is also a genuine reply (has a 'reply' e-tag)
      // should still appear — it's just an edited reply.
      const hasEditMarker = e.tags?.some((t: string[]) =>
        (t[0] === 'e' && t[3] === 'edit') || t[0] === '-edit-of'
      );
      const hasReplyTag = e.tags?.some((t: string[]) =>
        t[0] === 'e' && (t[3] === 'reply' || t[3] === 'root')
      );
      // If it has an edit marker but NO reply/root tags, it's an edited
      // top-level post leaking into the thread — filter it out.
      if (hasEditMarker && !hasReplyTag) return false;
      return true;
    });

    function directParentId(ev: any): string | null {
      const quotedIds = new Set(ev.tags.filter((t: string[]) => t[0] === 'q').map((t: string[]) => t[1]));
      // Exclude edit-reference e-tags — they aren't parent references
      const eTags = ev.tags.filter((t: string[]) => t[0] === 'e' && !quotedIds.has(t[1]) && t[3] !== 'edit');
      if (eTags.length === 0) return null;
      const replyTag = eTags.find((t: string[]) => t[3] === 'reply');
      if (replyTag) return replyTag[1];
      const rootTag = eTags.find((t: string[]) => t[3] === 'root');
      if (rootTag && eTags.length === 1) return rootTag[1];
      return eTags[eTags.length - 1][1];
    }

    const childrenOf = new Map<string, any[]>();
    const rootReplies: any[] = [];

    events.forEach(ev => {
      const parentId = directParentId(ev);
      if (!parentId || parentId === noteId) {
        rootReplies.push(ev);
      } else {
        const arr = childrenOf.get(parentId) || [];
        arr.push(ev);
        childrenOf.set(parentId, arr);
      }
    });

    rootReplies.sort((a, b) => a.created_at - b.created_at);
    childrenOf.forEach(arr => arr.sort((a, b) => a.created_at - b.created_at));
    return { rootReplies, childrenOf };
  }, [allRelated, noteId]);

  // Top-level reply composer state
  const [replyText, setReplyText] = useState('');
  const [posting, setPosting] = useState(false);
  const [attachments, setAttachments] = useState<{ url: string; type: 'image' | 'video' }[]>([]);
  const [showGifPicker, setShowGifPicker] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [compressing, setCompressing] = useState(false);
  const [compressionProgress, setCompressionProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { publish } = useNostrPublish();
  const textareaRef = useRef<MentionTextareaRef>(null);
  const [mentionedPubkeys, setMentionedPubkeys] = useState<{ pubkey: string; name: string }[]>([]);

  const insertEmoji = useCallback((emoji: string) => {
    const ta = textareaRef.current?.textarea;
    const pos = ta?.selectionStart ?? replyText.length;
    const newText = replyText.slice(0, pos) + emoji + replyText.slice(pos);
    setReplyText(newText);
    setShowEmojiPicker(false);
    setTimeout(() => {
      const newPos = pos + emoji.length;
      ta?.focus();
      ta?.setSelectionRange(newPos, newPos);
    }, 10);
  }, [replyText]);

  const handleTopReply = async () => {
    if (!user || (!replyText.trim() && attachments.length === 0) || !event) return;
    setPosting(true);
    try {
      const mediaUrls = attachments.map(a => a.url).join('\n');
      const fullContent = mediaUrls
        ? (replyText.trim() ? replyText + '\n\n' + mediaUrls : mediaUrls)
        : replyText.trim();

      const tags: string[][] = [];

      // If this post belongs to a community, publish the reply as a NIP-72
      // community comment (kind 1111) carrying the community `A`/`a` tag.
      // This keeps the comment scoped to the community so it's discoverable,
      // countable, and moderatable — instead of being an orphan kind 1 reply
      // that only lives in the raw thread. `communityId` is
      // "34550:<creator>:<dTag>".
      const communityParts = isCommunityPost && communityId ? communityId.split(':') : null;
      const communityCreator = communityParts?.[1] || '';
      const communityDTag = communityParts ? communityParts.slice(2).join(':') : '';
      const asCommunityComment = !!(isCommunityPost && communityCreator && communityDTag);

      // NIP-10: if the event we're replying to is itself a reply, include root tag
      const rootId = threadRootId || event.id;
      if (rootId !== event.id) {
        tags.push(['e', rootId, '', 'root']);
      }
      tags.push(['e', event.id, '', 'reply']);
      tags.push(['p', event.pubkey]);

      if (asCommunityComment) {
        // NIP-72 community reference tags (mirrors buildCommunityReplyTags)
        const aTag = communityATag(communityCreator, communityDTag);
        tags.push(['A', aTag]);
        tags.push(['a', aTag]);
        tags.push(['K', String(COMMUNITY_DEFINITION_KIND)]);
        tags.push(['k', String(event.kind)]);
      }

      // Add p-tags for mentioned users
      const seenPubkeys = new Set([event.pubkey]);
      mentionedPubkeys.forEach(m => {
        if (!seenPubkeys.has(m.pubkey)) {
          tags.push(['p', m.pubkey]);
          seenPubkeys.add(m.pubkey);
        }
      });
      attachments.forEach(att => {
        if (att.type === 'video') {
          tags.push(['imeta', `url ${att.url}`, 'm video/mp4']);
        } else {
          tags.push(['imeta', `url ${att.url}`]);
        }
      });

      await publish({
        kind: asCommunityComment ? COMMUNITY_POST_KIND : 1,
        content: fullContent,
        tags,
        created_at: Math.floor(Date.now() / 1000),
      });
      setReplyText('');
      setAttachments([]);
      setMentionedPubkeys([]);
      setShowGifPicker(false);
      setTimeout(refetch, 600);
    } catch {}
    setPosting(false);
  };

  const totalReplies = allRelated.length;

  return (
    <div className="max-w-2xl mx-auto">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background/90 backdrop-blur-md border-b border-border px-4 py-3 flex items-center gap-3">
        <button onClick={() => { window.history.length > 1 ? navigate(-1) : navigate('/'); }} className="p-1.5 rounded-xl hover:bg-muted transition-colors">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h1 className="text-base font-bold">
          {event && getParentEventId(event) ? 'Thread' : 'Post'}
        </h1>
        {totalReplies > 0 && (
          <span className="ml-auto text-xs text-muted-foreground">
            {totalReplies} {totalReplies === 1 ? 'reply' : 'replies'}
          </span>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-7 h-7 border-2 border-primary/20 border-t-primary rounded-full animate-spin" />
        </div>
      ) : event ? (
        <>
          {/* Ancestor thread chain — shows all parents up to the root */}
          <AncestorSection event={event} />

          {/* Current post — full PostCard (don't show ReplyTo since ancestors are above) */}
          <PostCard event={event} showReplyTo={false} />

          {/* ── Reply composer ── */}
          <div className="border-b border-border px-4 py-3">
            {communityReplyBlocked ? (
              <div className="text-center py-3">
                <p className="text-sm text-muted-foreground mb-1">This post belongs to a community</p>
                <p className="text-xs text-muted-foreground/70 mb-3">Only community members can reply</p>
                {communityId && (
                  <Link
                    to={`/community/${encodeURIComponent(communityId)}`}
                    className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90"
                  >
                    Join Community to Reply
                  </Link>
                )}
              </div>
            ) : user ? (
              <div className="flex gap-3">
                {user.picture ? (
                  <img src={user.picture} alt="" className="w-9 h-9 rounded-full object-cover shrink-0 mt-0.5" />
                ) : (
                  <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                    <span className="text-xs font-bold text-primary/60">{(user.name || '?')[0].toUpperCase()}</span>
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <MentionTextarea
                    ref={textareaRef}
                    value={replyText}
                    onChange={setReplyText}
                    placeholder="Post your reply…"
                    rows={2}
                    className="w-full bg-transparent text-sm placeholder:text-muted-foreground focus:outline-none resize-none leading-relaxed"
                    onKeyDown={e => {
                      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); handleTopReply(); }
                    }}
                    onMention={(pubkey, name) => {
                      setMentionedPubkeys(prev => prev.find(m => m.pubkey === pubkey) ? prev : [...prev, { pubkey, name }]);
                    }}
                  />

                  {/* Attachment previews */}
                  {attachments.length > 0 && (
                    <div className="flex gap-2 mt-2 flex-wrap">
                      {attachments.map((att, i) => (
                        <div key={i} className="relative group">
                          {att.type === 'video' ? (
                            <div className="w-20 h-20 rounded-lg bg-muted flex items-center justify-center border border-border">
                              <Film className="w-6 h-6 text-primary" />
                            </div>
                          ) : (
                            <img src={att.url} alt="" className="w-20 h-20 rounded-lg object-cover" />
                          )}
                          <button
                            onClick={() => setAttachments(prev => prev.filter((_, idx) => idx !== i))}
                            className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Compression progress */}
                  {compressing && (
                    <div className="mt-2 space-y-1">
                      <div className="flex items-center gap-2 text-xs text-amber-600 dark:text-amber-400">
                        <Loader2 className="w-3 h-3 animate-spin" />
                        <span>Compressing video… {compressionProgress}%</span>
                      </div>
                      <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                        <div className="h-full bg-amber-500 rounded-full transition-all" style={{ width: `${compressionProgress}%` }} />
                      </div>
                    </div>
                  )}

                  {/* Upload progress */}
                  {uploading && !compressing && (
                    <div className="mt-2 space-y-1">
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Loader2 className="w-3 h-3 animate-spin" />
                        <span>Uploading… {uploadProgress}%</span>
                      </div>
                      <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                        <div className="h-full bg-primary rounded-full transition-all" style={{ width: `${uploadProgress}%` }} />
                      </div>
                    </div>
                  )}

                  {/* GIF picker */}
                  {showGifPicker && (
                    <GifPicker
                      onSelect={(url) => {
                        setAttachments(prev => [...prev, { url, type: 'image' }]);
                        setShowGifPicker(false);
                      }}
                      onClose={() => setShowGifPicker(false)}
                    />
                  )}

                  {/* Action bar */}
                  <div className="flex items-center justify-between mt-2 pt-2 border-t border-border/40">
                    <div className="flex items-center gap-0.5">
                      {/* Image/video upload */}
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*,video/mp4,video/webm,video/quicktime"
                        className="hidden"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          e.target.value = '';
                          setCompressing(false);
                          setCompressionProgress(0);
                          setUploading(true);
                          setUploadProgress(0);
                          try {
                            const result = await uploadMedia(file, setUploadProgress, (cp) => {
                              if (cp.stage === 'loading' || cp.stage === 'compressing') {
                                setCompressing(true);
                                setCompressionProgress(cp.percent);
                              } else {
                                setCompressing(false);
                              }
                            });
                            setAttachments(prev => [...prev, result as { url: string; type: 'image' | 'video' }]);
                          } catch {}
                          setUploading(false);
                          setUploadProgress(0);
                          setCompressing(false);
                          setCompressionProgress(0);
                        }}
                      />
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading || compressing}
                        className="p-1.5 rounded-lg text-muted-foreground hover:text-primary hover:bg-primary/5 transition-colors"
                        title="Add image"
                      >
                        {(uploading || compressing) ? <Loader2 className="w-4 h-4 animate-spin" /> : <Image className="w-4 h-4" />}
                      </button>

                      {/* GIF */}
                      <button
                        onClick={() => { setShowGifPicker(p => !p); setShowEmojiPicker(false); }}
                        className={`p-1.5 rounded-lg transition-colors text-xs font-bold ${
                          showGifPicker ? 'text-primary bg-primary/10' : 'text-muted-foreground hover:text-primary hover:bg-primary/5'
                        }`}
                        title="Add GIF"
                      >
                        GIF
                      </button>

                      {/* Emoji */}
                      <div className="relative">
                        <button
                          onClick={() => { setShowEmojiPicker(p => !p); setShowGifPicker(false); }}
                          className={`p-1.5 rounded-lg transition-colors ${
                            showEmojiPicker ? 'text-amber-500 bg-amber-500/10' : 'text-muted-foreground hover:text-amber-500 hover:bg-amber-500/5'
                          }`}
                          title="Emoji"
                        >
                          <Smile className="w-4 h-4" />
                        </button>
                        {showEmojiPicker && (
                          <>
                            <div className="fixed inset-0 z-30" onClick={() => setShowEmojiPicker(false)} />
                            <EmojiPicker onSelect={insertEmoji} onClose={() => setShowEmojiPicker(false)} />
                          </>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <span className="text-[11px] text-muted-foreground hidden sm:inline">Ctrl+Enter to post</span>
                      <button
                        onClick={handleTopReply}
                        disabled={posting || uploading || compressing || (!replyText.trim() && attachments.length === 0)}
                        className="px-4 py-1.5 rounded-full bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 disabled:opacity-40 transition-all active:scale-95"
                      >
                        {posting ? (
                          <span className="flex items-center gap-1.5">
                            <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            Posting…
                          </span>
                        ) : 'Reply'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-3">
                <p className="text-sm text-muted-foreground mb-2">Log in to reply</p>
                <button
                  onClick={() => setShowLoginDialog(true)}
                  className="px-4 py-1.5 rounded-full bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90"
                >
                  Log In
                </button>
              </div>
            )}
          </div>

          {/* ── Replies ── */}
          {repliesLoading && rootReplies.length === 0 ? (
            <div className="flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground">
              <div className="w-4 h-4 border-2 border-muted-foreground/20 border-t-muted-foreground rounded-full animate-spin" />
              Loading replies…
            </div>
          ) : rootReplies.length === 0 ? (
            <div className="text-center py-14">
              <MessageCircle className="w-10 h-10 mx-auto text-muted-foreground/20 mb-2" />
              <p className="text-sm text-muted-foreground">No replies yet</p>
            </div>
          ) : (
            <div>
              {rootReplies.map(reply => (
                <ReplyThread
                  key={reply.id}
                  event={reply}
                  childrenOf={childrenOf}
                  onReplyPublished={refetch}
                  depth={0}
                  threadRootId={threadRootId}
                />
              ))}
            </div>
          )}
        </>
      ) : (
        <div className="text-center py-20">
          <p className="text-muted-foreground text-sm">Post not found.</p>
          <button onClick={() => { window.history.length > 1 ? navigate(-1) : navigate('/'); }} className="text-primary text-sm hover:underline mt-2 inline-block">← Back to feed</button>
        </div>
      )}
    </div>
  );
}

// ── ReplyThread ────────────────────────────────────────────────────────────────

function ReplyThread({
  event,
  childrenOf,
  onReplyPublished,
  depth,
  threadRootId,
}: {
  event: any;
  childrenOf: Map<string, any[]>;
  onReplyPublished: () => void;
  depth: number;
  threadRootId?: string;
}) {
  const { user, setShowLoginDialog } = useAppContext();
  const { publish } = useNostrPublish();
  const { profile } = useNostrProfile(event.pubkey);

  const [localLiked, setLocalLiked] = useState(false);
  const [replying, setReplying] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [posting, setPosting] = useState(false);
  const [showMenu, setShowMenu] = useState(false);

  // Translation state
  const [translation, setTranslation] = useState<TranslationResult | null>(null);
  const [translating, setTranslating] = useState(false);
  const [translateError, setTranslateError] = useState(false);
  const showTranslateButton = useMemo(() => mightNeedTranslation(event.content), [event.content]);

  // GIF + emoji state for inline reply
  const [attachments, setAttachments] = useState<{ url: string; type: 'image' | 'video' }[]>([]);
  const [showGifPicker, setShowGifPicker] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [compressing, setCompressing] = useState(false);
  const [compressionProgress, setCompressionProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const replyRef = useRef<MentionTextareaRef>(null);
  const [replyMentionedPubkeys, setReplyMentionedPubkeys] = useState<{ pubkey: string; name: string }[]>([]);

  // ── Fetch reactions (kind 7 likes, kind 6 reposts) from relays ──
  const { events: reactions } = useNostrQuery({
    filters: [{ kinds: [7, 6], '#e': [event.id], limit: 200 }],
    enabled: true,
  });

  // ── Fetch tip receipts (kind 9735) ──
  const tipSince = useMemo(() => Math.floor(Date.now() / 1000) - 86400 * 30, []);
  const { events: tipReceipts } = useNostrQuery({
    filters: [{ kinds: [9735], '#e': [event.id], limit: 20, since: tipSince }],
    enabled: true,
  });

  // ── Computed reaction stats ──
  const reactionEvents = useMemo(() => reactions.filter(e => e.kind === 7), [reactions]);
  const alreadyLiked = localLiked || (user ? reactionEvents.some(e => e.pubkey === user.pubkey) : false);
  const likeCount = reactionEvents.length + (localLiked && !reactionEvents.some(e => e.pubkey === user?.pubkey) ? 1 : 0);
  const repostCount = useMemo(() => reactions.filter(e => e.kind === 6).length, [reactions]);

  // Collect unique emoji reactions for display (top 3)
  const reactionEmojis = useMemo(() => {
    const counts: Record<string, number> = {};
    reactionEvents.forEach(e => {
      const emoji = e.content && e.content !== '+' ? e.content : '❤\uFE0F';
      counts[emoji] = (counts[emoji] || 0) + 1;
    });
    return Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, 3);
  }, [reactionEvents]);

  // ── Tip social proof ──
  // Use the shared summary so token tips (FURU/PUSD/…) show their icon +
  // amount here too, instead of reading "0 sats" like the old BCH-only sum.
  const tipCount = tipReceipts.length;
  const [tipTokensResolved, setTipTokensResolved] = useState(false);
  const { tipTotal, tipUsdTotal, hasTokenTip, tokenTips } = useMemo(
    () => computeTipSummary(tipReceipts),
    [tipReceipts, tipTokensResolved],
  );
  const tipperPubkeys = useMemo(() => [...new Set(tipReceipts.map(e => e.pubkey))].slice(0, 3), [tipReceipts]);

  // Resolve the tip-token allowlist once a token tip is present so its
  // icon/name/whole-amount can render (cached module-wide).
  useEffect(() => {
    if (tipTokensResolved || !hasTokenTip) return;
    let alive = true;
    resolveTipTokens().then(() => { if (alive) setTipTokensResolved(true); }).catch(() => {});
    return () => { alive = false; };
  }, [hasTokenTip, tipTokensResolved]);

  const children = childrenOf.get(event.id) || [];
  const hasChildren = children.length > 0;

  const isNested = depth > 0;

  const displayName = useDisplayName(event.pubkey, profile, event.pubkey.slice(0, 8) + '…');
  const npub = (() => { try { return nip19.npubEncode(event.pubkey); } catch { return ''; } })();

  useEffect(() => {
    if (replying && replyRef.current) replyRef.current.focus();
  }, [replying]);

  const insertEmoji = useCallback((emoji: string) => {
    const ta = replyRef.current?.textarea;
    const pos = ta?.selectionStart ?? replyText.length;
    const newText = replyText.slice(0, pos) + emoji + replyText.slice(pos);
    setReplyText(newText);
    setShowEmojiPicker(false);
    setTimeout(() => {
      const newPos = pos + emoji.length;
      ta?.focus();
      ta?.setSelectionRange(newPos, newPos);
    }, 10);
  }, [replyText]);

  const handleLike = async () => {
    if (!user) { setShowLoginDialog(true); return; }
    if (alreadyLiked) return;
    setLocalLiked(true);
    await publish({
      kind: 7, content: '+',
      tags: [['e', event.id], ['p', event.pubkey]],
      created_at: Math.floor(Date.now() / 1000),
    });
  };

  const handleReply = async () => {
    if (!user || (!replyText.trim() && attachments.length === 0)) return;
    setPosting(true);
    try {
      const mediaUrls = attachments.map(a => a.url).join('\n');
      const fullContent = mediaUrls
        ? (replyText.trim() ? replyText + '\n\n' + mediaUrls : mediaUrls)
        : replyText.trim();

      const tags: string[][] = [];

      // If the comment we're replying to is itself a community comment
      // (kind 1111 carrying the community `A`/`a` tag), inherit that
      // community reference so the nested reply stays scoped to the
      // community too. This keeps entire comment threads community-scoped.
      const parentCommunityATag = event.tags?.find(
        (t: string[]) => (t[0] === 'A' || t[0] === 'a') && t[1]?.startsWith(`${COMMUNITY_DEFINITION_KIND}:`)
      )?.[1] as string | undefined;
      const asCommunityComment = !!parentCommunityATag;

      // NIP-10: include root tag for nested replies
      if (threadRootId && threadRootId !== event.id) {
        tags.push(['e', threadRootId, '', 'root']);
      }
      tags.push(['e', event.id, '', 'reply']);
      tags.push(['p', event.pubkey]);

      if (asCommunityComment && parentCommunityATag) {
        tags.push(['A', parentCommunityATag]);
        tags.push(['a', parentCommunityATag]);
        tags.push(['K', String(COMMUNITY_DEFINITION_KIND)]);
        tags.push(['k', String(event.kind)]);
      }

      // Add p-tags for mentioned users
      const seenPks = new Set([event.pubkey]);
      replyMentionedPubkeys.forEach(m => {
        if (!seenPks.has(m.pubkey)) {
          tags.push(['p', m.pubkey]);
          seenPks.add(m.pubkey);
        }
      });
      attachments.forEach(att => {
        if (att.type === 'video') {
          tags.push(['imeta', `url ${att.url}`, 'm video/mp4']);
        } else {
          tags.push(['imeta', `url ${att.url}`]);
        }
      });

      await publish({
        kind: asCommunityComment ? COMMUNITY_POST_KIND : 1,
        content: fullContent,
        tags,
        created_at: Math.floor(Date.now() / 1000),
      });
      setReplyText('');
      setAttachments([]);
      setReplyMentionedPubkeys([]);
      setReplying(false);
      setShowGifPicker(false);
      setTimeout(onReplyPublished, 600);
    } catch {}
    setPosting(false);
  };

  return (
    <div className={isNested ? 'ml-10 pl-0' : ''}>
      <div className="flex gap-3 px-4 pt-3 pb-1 hover:bg-muted/20 transition-colors group relative">
        {/* Avatar column */}
        <div className="flex flex-col items-center shrink-0">
          <Link to={`/profile/${event.pubkey}`}>
            {profile?.picture ? (
              <img src={profile.picture} alt="" className="w-9 h-9 rounded-full object-cover hover:opacity-80 transition-opacity" />
            ) : (
              <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center">
                <span className="text-xs font-bold text-primary/60">{displayName[0]?.toUpperCase()}</span>
              </div>
            )}
          </Link>
          {hasChildren && (
            <div className="w-0.5 flex-1 bg-border/60 mt-1 mb-0 min-h-[20px]" />
          )}
        </div>

        {/* Content column */}
        <div className="flex-1 min-w-0 pb-2">
          {/* Name + time + menu */}
          <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
            <Link to={`/profile/${event.pubkey}`} className="font-semibold text-sm hover:underline leading-none">
              {displayName}
            </Link>
            <UserBadges pubkey={event.pubkey} size="sm" />
            <span className="text-xs text-muted-foreground ml-auto shrink-0">{timeAgo(event.created_at)}</span>
            <button
              onClick={() => setShowMenu(v => !v)}
              className="opacity-0 group-hover:opacity-100 p-0.5 rounded hover:bg-muted transition-all"
            >
              <MoreHorizontal className="w-3.5 h-3.5 text-muted-foreground" />
            </button>
            {showMenu && (
              <div className="absolute right-4 top-8 bg-popover border border-border rounded-lg shadow-lg z-20 py-1 w-44"
                onMouseLeave={() => setShowMenu(false)}>
                <button
                  onClick={() => { navigator.clipboard.writeText(event.id); setShowMenu(false); }}
                  className="w-full text-left px-3 py-2 text-xs hover:bg-muted"
                >
                  Copy note ID
                </button>
                <Link
                  to={`/note/${event.id}`}
                  className="block px-3 py-2 text-xs hover:bg-muted"
                  onClick={() => setShowMenu(false)}
                >
                  Open in thread view
                </Link>
              </div>
            )}
          </div>

          {/* Post text */}
          <NoteContent content={event.content} tags={event.tags} collapsible collapseHeight={560} />

          {/* Translation */}
          {translation && (
            <div className="mt-1.5 pt-1.5 border-t border-border/50">
              <div className="flex items-center gap-1.5 mb-1">
                <Languages className="w-3 h-3 text-primary/60" />
                <span className="text-[10px] text-muted-foreground">
                  Translated from {getLanguageName(translation.detectedLang)}
                </span>
                <button
                  onClick={() => setTranslation(null)}
                  className="text-[10px] text-primary hover:underline ml-auto"
                >
                  Show original
                </button>
              </div>
              <p className="text-sm leading-relaxed whitespace-pre-wrap break-words">
                {translation.translatedText}
              </p>
            </div>
          )}
          {!translation && showTranslateButton && (
            <button
              onClick={async () => {
                if (translating) return;
                setTranslating(true);
                setTranslateError(false);
                try {
                  const result = await translateText(event.content);
                  if (result.detectedLang !== result.targetLang) {
                    setTranslation(result);
                  }
                } catch {
                  setTranslateError(true);
                  setTimeout(() => setTranslateError(false), 3000);
                } finally {
                  setTranslating(false);
                }
              }}
              className="flex items-center gap-1 mt-1 text-[11px] text-primary/70 hover:text-primary transition-colors"
            >
              <Languages className="w-3 h-3" />
              {translating ? 'Translating...' : translateError ? 'Translation failed' : 'Translate'}
            </button>
          )}

          {/* Action row */}
          <div className="flex items-center gap-0.5 mt-2 -ml-1.5 flex-wrap">
            {/* Reply */}
            <button
              onClick={() => { if (!user) { setShowLoginDialog(true); return; } setReplying(r => !r); }}
              className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-xs transition-colors ${
                replying ? 'text-primary bg-primary/8' : 'text-muted-foreground hover:text-primary hover:bg-primary/8'
              }`}
            >
              <MessageCircle className="w-3.5 h-3.5" />
              {children.length > 0 && <span>{children.length}</span>}
            </button>

            {/* Repost count */}
            {repostCount > 0 && (
              <span className="flex items-center gap-1 px-2 py-1 rounded-full text-xs text-emerald-500">
                <Repeat2 className="w-3.5 h-3.5" />
                <span>{repostCount}</span>
              </span>
            )}

            {/* Reactions / Like */}
            <button
              onClick={handleLike}
              disabled={alreadyLiked}
              className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs transition-colors ${
                alreadyLiked ? 'text-red-500' : 'text-muted-foreground hover:text-red-500 hover:bg-red-500/8'
              }`}
            >
              <Heart className={`w-3.5 h-3.5 ${alreadyLiked ? 'fill-current' : ''}`} />
              {reactionEmojis.length > 0 && (
                <span className="flex items-center gap-0.5">
                  {reactionEmojis.map(([emoji]) => (
                    <span key={emoji} className="text-[11px]">{emoji === '❤\uFE0F' ? '' : emoji}</span>
                  ))}
                  <span>{likeCount}</span>
                </span>
              )}
              {reactionEmojis.length === 0 && likeCount > 0 && <span>{likeCount}</span>}
            </button>

            {/* Tip button + social proof */}
            <div className="flex items-center gap-0.5">
              <BCHTipButton recipientPubkey={event.pubkey} recipientProfile={profile} eventId={event.id} />
              {tipCount > 0 && (
                <TipSocialProof
                  tipTotal={tipTotal}
                  tipUsdTotal={tipUsdTotal}
                  hasTokenTip={hasTokenTip}
                  tokenTips={tokenTips}
                  tipperPubkeys={tipperPubkeys}
                />
              )}
            </div>
          </div>

          {/* Inline reply composer */}
          {replying && (
            <div className="mt-2 flex gap-2.5">
              {user?.picture ? (
                <img src={user.picture} alt="" className="w-7 h-7 rounded-full object-cover shrink-0 mt-0.5" />
              ) : (
                <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                  <span className="text-[10px] font-bold text-primary/60">{(user?.name || '?')[0]?.toUpperCase()}</span>
                </div>
              )}
              <div className="flex-1 min-w-0 rounded-2xl border border-border bg-muted/30 px-3 py-2">
                <MentionTextarea
                  ref={replyRef}
                  value={replyText}
                  onChange={setReplyText}
                  placeholder={`Reply to ${displayName}…`}
                  rows={2}
                  className="w-full bg-transparent text-sm placeholder:text-muted-foreground focus:outline-none resize-none leading-relaxed"
                  autoFocus
                  onKeyDown={e => {
                    if (e.key === 'Escape') { setReplying(false); setReplyText(''); setAttachments([]); }
                    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); handleReply(); }
                  }}
                  onMention={(pubkey, name) => {
                    setReplyMentionedPubkeys(prev => prev.find(m => m.pubkey === pubkey) ? prev : [...prev, { pubkey, name }]);
                  }}
                />

                {/* Attachment previews */}
                {attachments.length > 0 && (
                  <div className="flex gap-2 mt-2 flex-wrap">
                    {attachments.map((att, i) => (
                      <div key={i} className="relative group/att">
                        {att.type === 'video' ? (
                          <div className="w-16 h-16 rounded-lg bg-muted flex items-center justify-center border border-border">
                            <Film className="w-5 h-5 text-primary" />
                          </div>
                        ) : (
                          <img src={att.url} alt="" className="w-16 h-16 rounded-lg object-cover" />
                        )}
                        <button
                          onClick={() => setAttachments(prev => prev.filter((_, idx) => idx !== i))}
                          className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover/att:opacity-100 transition-opacity"
                        >
                          <X className="w-2.5 h-2.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Compression progress */}
                {compressing && (
                  <div className="mt-1.5 flex items-center gap-2 text-xs text-amber-600 dark:text-amber-400">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    <span>Compressing… {compressionProgress}%</span>
                  </div>
                )}

                {/* Upload progress */}
                {uploading && !compressing && (
                  <div className="mt-1.5 flex items-center gap-2 text-xs text-muted-foreground">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    <span>Uploading… {uploadProgress}%</span>
                  </div>
                )}

                {/* GIF picker */}
                {showGifPicker && (
                  <GifPicker
                    onSelect={(url) => {
                      setAttachments(prev => [...prev, { url, type: 'image' }]);
                      setShowGifPicker(false);
                    }}
                    onClose={() => setShowGifPicker(false)}
                  />
                )}

                {/* Action bar */}
                <div className="flex items-center justify-between mt-1.5 pt-1.5 border-t border-border/40">
                  <div className="flex items-center gap-0.5">
                    {/* Image upload */}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*,video/mp4,video/webm,video/quicktime"
                      className="hidden"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        e.target.value = '';
                        setCompressing(false);
                        setCompressionProgress(0);
                        setUploading(true);
                        setUploadProgress(0);
                        try {
                          const result = await uploadMedia(file, setUploadProgress, (cp) => {
                            if (cp.stage === 'loading' || cp.stage === 'compressing') {
                              setCompressing(true);
                              setCompressionProgress(cp.percent);
                            } else {
                              setCompressing(false);
                            }
                          });
                          setAttachments(prev => [...prev, result as { url: string; type: 'image' | 'video' }]);
                        } catch {}
                        setUploading(false);
                        setUploadProgress(0);
                        setCompressing(false);
                        setCompressionProgress(0);
                      }}
                    />
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading || compressing}
                      className="p-1 rounded-lg text-muted-foreground hover:text-primary hover:bg-primary/5 transition-colors"
                      title="Add image"
                    >
                      {(uploading || compressing) ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Image className="w-3.5 h-3.5" />}
                    </button>

                    {/* GIF */}
                    <button
                      onClick={() => { setShowGifPicker(p => !p); setShowEmojiPicker(false); }}
                      className={`px-1.5 py-1 rounded-lg transition-colors text-[10px] font-bold ${
                        showGifPicker ? 'text-primary bg-primary/10' : 'text-muted-foreground hover:text-primary hover:bg-primary/5'
                      }`}
                      title="Add GIF"
                    >
                      GIF
                    </button>

                    {/* Emoji */}
                    <div className="relative">
                      <button
                        onClick={() => { setShowEmojiPicker(p => !p); setShowGifPicker(false); }}
                        className={`p-1 rounded-lg transition-colors ${
                          showEmojiPicker ? 'text-amber-500 bg-amber-500/10' : 'text-muted-foreground hover:text-amber-500 hover:bg-amber-500/5'
                        }`}
                        title="Emoji"
                      >
                        <Smile className="w-3.5 h-3.5" />
                      </button>
                      {showEmojiPicker && (
                        <>
                          <div className="fixed inset-0 z-30" onClick={() => setShowEmojiPicker(false)} />
                          <EmojiPicker onSelect={insertEmoji} onClose={() => setShowEmojiPicker(false)} />
                        </>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => { setReplying(false); setReplyText(''); setAttachments([]); setShowGifPicker(false); }}
                      className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleReply}
                      disabled={posting || uploading || compressing || (!replyText.trim() && attachments.length === 0)}
                      className="flex items-center gap-1 px-3 py-1 rounded-full bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 disabled:opacity-40 transition-all active:scale-95"
                    >
                      {posting
                        ? <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        : <Send className="w-3 h-3" />}
                      Reply
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Children */}
      {hasChildren && (
        <div>
          {children.map(child => (
            <ReplyThread
              key={child.id}
              event={child}
              childrenOf={childrenOf}
              onReplyPublished={onReplyPublished}
              depth={depth + 1}
              threadRootId={threadRootId}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ── GIF Picker ────────────────────────────────────────────────────────────────

// GifPicker is now imported from ../components/GifPicker

